import React, {Component, Fragment} from "react"
import PropTypes from "prop-types"
import {get, sumBy, omit, filter} from "lodash"
import styled from "styled-components"
import {withRouter} from "react-router"

import id from "@irati/utils/id"
import {injectIntl, FormattedMessage} from "@irati/utils/lang"
import HeatmapQuery from "@irati/insights/apollo/query/heatmap/get"
import {AnalysisContext} from "@irati/insights/context"
import {RouterLink} from "@irati/ui/src/Link"
import {Card} from "@irati/ui/src/Layout"
import {getStandardDeviation} from "@irati/insights/utils/math"
import {injectRouteWithQuery} from "@irati/utils/helpers/routing"
import {overrideAuth} from "@irati/insights/utils/apollo"
import {doIHavePermission} from "@irati/utils/permissions/HasPermissionTo"
import auth from "@irati/auth"

import Table, {Tr} from "../../ui/SimpleTable"
import filterStats from "./helpers/filterStats"
import Score from "./Score"
import BaseCell from "./BaseCell"
import {SectionTitle} from "../Analysis"
import {DriverCellRenderer} from "../Renderers"
import {CELL_WIDTH} from "../../utils/constants/heatmap"
import TableSpacer from "./TableSpacer"
import addMissingStats from "./helpers/addMissingStats"
import sitemap from "../../routes/sitemap"
import Toggle from "./Toggle"
import FlexWrapper from "../../ui/Layout/FlexWrapper"

const TeamCell = styled(BaseCell)`
  max-width: ${CELL_WIDTH}px;
  height:50px;
  text-align: right;
  padding-right: 15px!important;
`

const StyledTable = styled(Table)`
  border-collapse: separate;
  border-spacing: 7px;
`

const Header = styled(DriverCellRenderer)`
  width:${CELL_WIDTH}px;
  height: ${CELL_WIDTH}px;
  flex-direction: column;
  align-items: center;
  justify-content: space-evenly;
  > p {
    color: #333;
    text-transform: none;
    font-size: 12px;
    text-align: center;
  }
  > img {
    margin-right:0!important
  }
`

const SpacedCard = styled(Card)`
  margin-bottom: 20px;
`


class Heatmap extends Component {
  static propTypes = {
    formatMessage: PropTypes.func.isRequired,
    match: PropTypes.object.isRequired
  }

  state = {showCOT: false}

  getHeadings = (row) => {
    const headings = row.map((item, i) => ({
      title: <Header row={item} accessor="driver" />,
      key: i,
      style: {padding: 0, width: 90} // the only way to style the table header
    }))
    headings.unshift({title: null})
    return headings
  }

  getAverage = (row) => {
    return sumBy(row, (stat) => parseFloat(stat.averageScore, 10)) / row.filter((item) => item.averageScore !== 0).length
  }

  isStatHighlighted = ({driverId, teamId}, {driverId: filteredDriverId, teamId: filteredTeamId}) => {
    const isDriverMatch = filteredDriverId && filteredDriverId === driverId
    const isTeamMatch = filteredTeamId && filteredTeamId === teamId

    /** not filtered by team or driver */
    if (!filteredDriverId && !filteredTeamId) return true

    /** filtered by both driver and team */
    if (filteredDriverId && filteredTeamId) {
      return isDriverMatch && isTeamMatch
    }

    /** filtered by driver */
    if (isDriverMatch) return true

    /** filtered by team */
    return isTeamMatch
  }


  filterTeamsByAnalysisFilter = (rows, {teamId, driverId}) => {
    /** Not filtered, return all rows */
    if (!driverId && !teamId) return rows

    /** Filtered by driver only */
    if (driverId && !teamId) return [rows[0]]

    /** filtered by team */
    return filter(rows, (row, index) => {
      /** always return all teams */
      if (index === 0) return true

      return get(row, "0.teamId") === teamId
    })
  }

  handleToggle = () => {
    this.setState((prevState) => ({showCOT: !prevState.showCOT}))
  }


  render() {
    const idNamespace = id("Heatmap", this.props)
    const {formatMessage, match} = this.props
    const {showCOT} = this.state
    const showCOTFeature = doIHavePermission(["insights.changeOverTime"], auth.apps())
    return (
      <AnalysisContext.Consumer>
        {({teams, schedule, filters, isDemo, demoTenantAccessToken, tenantId: tenant}) => {
          const {teamId, driverId} = filters
          const {drivers: {results: drivers}, surveyCount} = schedule
          return (
            <Fragment>
              <FlexWrapper justify="space-between">
                <SectionTitle
                  id={id("title", idNamespace)}
                  title={formatMessage({id: "insights.heatmap.title"})} />
                {surveyCount > 1 && <Toggle onClick={this.handleToggle} active={showCOT} />}
              </FlexWrapper>
              <HeatmapQuery
                cycle={schedule}
                tenant={tenant}
                filters={filters}
                withHistoricalData={showCOTFeature}
                {...overrideAuth(demoTenantAccessToken, isDemo)}>
                {({data: {results}, headerData, historicalLoading}) => {
                  const data = addMissingStats(results, teams, drivers)
                  let rows = filterStats(data, headerData, drivers, formatMessage)
                  rows = this.filterTeamsByAnalysisFilter(rows, filters)
                  const average = this.getAverage(rows[0])
                  const headings = this.getHeadings(rows[0])
                  const standardDeviation = getStandardDeviation(results.map(({averageScore}) => averageScore)) || 0
                  const isFiltered = teamId || driverId
                  const resetQueryParams = omit(filters, ["teamId", "driverId"])

                  return (
                    <Fragment>
                      <SpacedCard id={id("card", idNamespace)}>
                        <StyledTable responsive headings={headings}>
                          {rows.map((row, index) => {
                            const team = get(row, "[0].team.public_name")
                            const isFirst = index === 0
                            const isLast = index >= rows.length - 1
                            return (
                              <Fragment key={index}>
                                <Tr>
                                  <TeamCell>{team}</TeamCell>
                                  {row.map((stat) => {
                                    const scoreKey = `score_driver_${stat.driverId}_team_${get(stat, "team.uuid")}`
                                    return (
                                      <Score
                                        id={id(scoreKey, idNamespace)}
                                        key={scoreKey}
                                        stat={stat}
                                        average={average}
                                        standardDeviation={standardDeviation}
                                        historicalLoading={historicalLoading}
                                        showCOT={showCOT}
                                        isHighlighted={this.isStatHighlighted(stat, {teamId, driverId})} />
                                    )
                                  })}
                                </Tr>
                                {isFirst && !isLast && <TableSpacer colSpan={row.length} />}
                              </Fragment>
                            )
                          })}
                        </StyledTable>
                      </SpacedCard>

                      {isFiltered && (
                        <RouterLink
                          id={id("view-full-heatmap", idNamespace)}
                          uppercase={false}
                          to={injectRouteWithQuery(sitemap.analysis.url, {
                            params: {...match.params},
                            queryParams: resetQueryParams
                          })}>
                          <FormattedMessage id="insights.heatmap.viewFullHeatmap" />
                        </RouterLink>
                      )}
                    </Fragment>
                  )
                }}
              </HeatmapQuery>
            </Fragment>
          )
        }}
      </AnalysisContext.Consumer>
    )
  }
}

export default injectIntl(withRouter(Heatmap))
